import MapIcon from 'components/elements/icons/MapIcon'
import Text from 'components/elements/texts/Text'
import MainTemplate from 'components/templates/MainTemplate'
import { PAGE_PATH } from 'constants/pagePath'
import { HistoryStackContext } from 'contexts/HistoryStackContext'
import React, { useCallback, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { transitionWithAction } from 'utils/commonUtils'
import { getDomain } from 'utils/domain'
import DOMAIN from 'constants/domains'
import { CLIENT_SETTINGS } from '../../constants/clientSettings'
import InformationModal from 'components/elements/modal/InformationModal'
import { Frame } from 'components/elements/Frame'
import Button from 'components/elements/buttons/Button'
import { isSjDomain } from 'utils/domain'

/** 対象外ポップアップのコンテンツ */
const CautionMessage: React.FC = React.memo(function cautionMessage() {
  return (
    <div style={{ textAlign: 'left', padding: '0px 30px' }}>
      <div>下記に当てはまる場合は、</div>
      <div style={{ marginBottom: '28px' }}>
        ロードアシスタンスの対象外となります。
      </div>
      {CLIENT_SETTINGS.messages.consent.caution.map(
        (message: string, i: number) => {
          return <CautionMessageStyle key={i}>{message}</CautionMessageStyle>
        }
      )}
    </div>
  )
})

/**
 * QN-02: スタート画面
 */
const Start: React.FC = () => {
  /** 次の画面に進むボタンのラベル */
  const START_BUTTON_LABEL = '今すぐスタッフを呼ぶ'
  /** 予約画面に遷移するボタンのラベル */
  const RESERVATION_BUTTON_LABEL = '日時を指定して予約する'

  /** React routerの遷移オブジェクト */
  const history = useHistory()

  /** 操作履歴のコンテキスト */
  const historyStackContext = useContext(HistoryStackContext)

  /** 対象外ポップアップの開閉フラグ */
  const [isOpenCaution, setIsOpenCaution] = React.useState<boolean>(false)

  /** 次の画面に進むページのパス */
  const [nextPath, setNextPath] = React.useState<string>()

  const transitionPage = useCallback((nextPagePath: string): void => {
    transitionWithAction(historyStackContext, START_BUTTON_LABEL, () => {
      history.push(nextPagePath)
    })
  }, [])

  /** スタート画面で使用するスタイル */
  const START_SCREEN_STYLE = React.useMemo(() => {
    return {
      [DOMAIN.SJ]: {
        /** '【安全確保のお願い】'とその下の説明文のテキストカラー */
        textColor: '#cc0022',
        /** '今すぐスタッフを呼ぶ'ボタンのテキストカラー */
        startButtonTextColor: '#FFFFFF',
        /**'コールセンターに電話をかける' ボタンのテキストカラー  */
        callButtonTextColor: '#cc0022',
        /** ヘルプの説明文と 'ヘルプボタン' のテキストカラー */
        helpTextColor: '#222222',
        /** ドメインごとのFrameのスタイル */
        frameStyle: {
          border: '2px #cc0022 solid',
          padding: '8px 8px 0px',
        },
        /** ドメインごとの架電ボタンのスタイル */
        callButtonStyle: {
          background: '#FFFFFF',
          border: '2px solid #cc0022',
        },
        /** 今すぐスタッフを呼ぶ、日時を指定して予約するボタンのスタイル */
        buttonStyle: {
          background: '#cc0022',
          color: '#FFFFFF',
        },
      },
      [DOMAIN.SAFIC]: {
        /** '【安全確保のお願い】'とその下の説明文のテキストカラー */
        textColor: '#FFFFFF',
        /** '今すぐスタッフを呼ぶ'ボタンのテキストカラー */
        startButtonTextColor: ' #f08200',
        /** '今すぐスタッフを呼ぶ'ボタンのテキストカラー */
        callButtonTextColor: ' #f08200',
        /** ヘルプの説明文と 'ヘルプボタン' のテキストカラー */
        helpTextColor: '#FFFFFF',
        /** ドメインごとのFrameのスタイル */
        frameStyle: {
          border: '2px #FFFFFF solid',
          padding: '8px 8px 0px',
        },
        /** ドメインごとの架電ボタンのスタイル */
        callButtonStyle: {
          background: '#FFFFFF',
          border: '2px solid #FFFFFF',
        },
        /** 今すぐスタッフを呼ぶ、日時を指定して予約するボタンのスタイル */
        buttonStyle: {
          background: '#FFFFFF',
          color: '#f08200',
        },
      },
    }[getDomain()]
  }, [])

  return (
    <MainTemplate
      title=""
      content={
        START_SCREEN_STYLE && (
          <>
            {nextPath && (
              <InformationModal
                on={isOpenCaution}
                content={<CautionMessage />}
                greyBack
                modalFill
                textCenter
                closeLabel="確認して手配に進む"
                closeFunction={(): void => {
                  transitionPage(nextPath)
                }}
              />
            )}
            <Frame {...START_SCREEN_STYLE.frameStyle}>
              <Text
                color={START_SCREEN_STYLE.textColor}
                bold
                textAlign="center"
                fontSize="26px"
              >
                {CLIENT_SETTINGS.messages.consent.title}
              </Text>
              {/*  余白調整、上のマージ空ける */}
              <div style={{ marginBottom: '8px' }} />

              {CLIENT_SETTINGS.messages.consent.description.map(
                (text: string, i: number) => (
                  <Text color={START_SCREEN_STYLE.textColor} bold key={i}>
                    {text}
                  </Text>
                )
              )}
            </Frame>
            <Criterion>
              <Text color={START_SCREEN_STYLE.helpTextColor}>
                {CLIENT_SETTINGS.messages.consent.criterion}
              </Text>
            </Criterion>
            <Button
              {...START_SCREEN_STYLE.buttonStyle}
              onClick={(): void => {
                // SJクライアントはQN-03(車の状況画面)に遷移
                // SAFICクライアントは対象外ポップアップを表示、ポップアップを閉じたあとにQN-03(車の状況画面)に遷移
                if (getDomain() === DOMAIN.SJ) {
                  getDomain() === DOMAIN.SJ &&
                    transitionPage(PAGE_PATH.VEHICLE_STATE)
                  return
                }
                setIsOpenCaution(!isOpenCaution)
                setNextPath(PAGE_PATH.VEHICLE_STATE)
                return
              }}
              withArrow={isSjDomain()}
            >
              {START_BUTTON_LABEL}
            </Button>
            <GrayBox>
              <GrayBoxContentArea>
                <GrayBoxRelativeArea>
                  <DescriptionText>
                    {CLIENT_SETTINGS.messages.consent.mapDescription}
                  </DescriptionText>
                  <MapDraw>
                    <MapIcon />
                  </MapDraw>
                </GrayBoxRelativeArea>
              </GrayBoxContentArea>
            </GrayBox>
            <Button
              {...START_SCREEN_STYLE.buttonStyle}
              onClick={(): void => {
                // SJクライアントはQN-02-a(予約画面)に遷移
                // SAFICクライアントは対象外ポップアップを表示、ポップアップを閉じたあとにQN-02-a(予約画面)に遷移
                if (getDomain() === DOMAIN.SJ) {
                  transitionPage(PAGE_PATH.RESERVATION)
                  return
                }
                setNextPath(PAGE_PATH.RESERVATION)
                setIsOpenCaution(!isOpenCaution)
                return
              }}
              withArrow={isSjDomain()}
            >
              {RESERVATION_BUTTON_LABEL}
            </Button>
          </>
        )
      }
    />
  )
}

const GrayBox = styled.div`
  position: relative;
  left: -8px;
  width: 100vw;
  height: 184px;
  background: var(--color-background);
`

const GrayBoxContentArea = styled.div`
  position: relative;
  width: 268px;
  height: 146px;
  margin: 0 auto;
  padding: 24px 0 14px;
`

const GrayBoxRelativeArea = styled.div`
  position: relative;
  width: 268px;
  height: 146px;
`

const DescriptionText = styled(Text)`
  position: absolute;
  top: 22px;
  font-weight: bold;
  font-size: 12px;
  line-height: 1.5;
  text-align: left;
`

const MapDraw = styled.div`
  position: absolute;
  top: -5px;
  left: 120px;
`

const Criterion = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  margin: 16px 0px -8px;
`

const CautionMessageStyle = styled.div`
  text-indent: -1em;
  &:before {
    content: '・';
  }
`

export default Start
